import type dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

type UseBufferedDatePickerValueOptions = {
  value: dayjs.Dayjs | null;
  onChange: (newValue: dayjs.Dayjs | null) => void;
  onBlur?: () => void;
};

const isSameDate = (a: dayjs.Dayjs | null, b: dayjs.Dayjs | null): boolean => {
  if (a === null || b === null) {
    return a === b;
  }
  return a.isValid() && b.isValid() && a.isSame(b);
};

/**
 * Buffers the picker value while focused so partial keyboard input stays visible
 * when the parent only commits valid dates.
 */
export const useBufferedDatePickerValue = ({
  value,
  onChange,
  onBlur,
}: UseBufferedDatePickerValueOptions) => {
  const isFocusedRef = useRef(false);
  const pickerValueRef = useRef(value);
  const [pickerValue, setPickerValue] = useState(value);

  useEffect(() => {
    const currentValue = pickerValueRef.current;
    const hasInvalidPartialInput =
      isFocusedRef.current && currentValue !== null && !currentValue.isValid();

    // Callers parse a fresh Dayjs each render, so skip unchanged dates: re-syncing them queued an
    // update per render, which chained into "Maximum update depth exceeded" while typing (LLX-4088).
    if (!hasInvalidPartialInput && !isSameDate(currentValue, value)) {
      pickerValueRef.current = value;
      setPickerValue(value);
    }
  }, [value]);

  const markFocused = () => {
    isFocusedRef.current = true;
  };

  const handleChange = (newValue: dayjs.Dayjs | null) => {
    pickerValueRef.current = newValue;
    setPickerValue(newValue);

    if (newValue === null || newValue.isValid()) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;

    const currentValue = pickerValueRef.current;
    if (currentValue !== null && !currentValue.isValid()) {
      pickerValueRef.current = value;
      setPickerValue(value);
    }

    onBlur?.();
  };

  return {
    pickerValue,
    handleChange,
    handleFocus: markFocused,
    handleBlur,
  };
};
